import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiMail, FiCheck, FiHeart, FiActivity, FiBookOpen, FiMapPin } from 'react-icons/fi';
import { subscriberAPI } from '../services/api';
import { useAnalytics } from '../context/AnalyticsContext';
import toast from 'react-hot-toast';
import './SubscribePopup.css';

const interests = [
  { id: 'health_tips', label: 'Health Tips', icon: FiHeart },
  { id: 'news', label: 'Medical News', icon: FiBookOpen },
  { id: 'telehealth', label: 'Telehealth Updates', icon: FiActivity },
  { id: 'wellness', label: 'Wellness', icon: FiHeart },
  { id: 'nutrition', label: 'Nutrition', icon: FiHeart },
  { id: 'traditional_medicine', label: 'Traditional Medicine', icon: FiActivity }
];

const SubscribePopup = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedInterests, setSelectedInterests] = useState(['health_tips', 'news']);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const { markAsSubscribed, trackSubscribe } = useAnalytics();

  // Try to get user location when popup opens
  useEffect(() => {
    if (isOpen && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}`
              );
              const data = await response.json();
              if (data.results && data.results[0]) {
                const components = data.results[0].address_components;
                const city = components.find(c => c.types.includes('locality'))?.long_name || '';
                const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                const country = components.find(c => c.types.includes('country'))?.long_name || '';
                setUserLocation({ city: city + (state ? `, ${state}` : ''), country });
              }
            } catch {
              // Silently fail - location is optional
            }
          },
          () => {
            // User denied or error - that's fine
          },
          { timeout: 5000 }
        );
      }
    }
  }, [isOpen, userLocation]);

  const toggleInterest = (id) => {
    setSelectedInterests(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    if (selectedInterests.length === 0) {
      toast.error('Please select at least one interest');
      return;
    }

    setLoading(true);

    try {
      await subscriberAPI.subscribe({
        email,
        name,
        interests: selectedInterests,
        source: 'popup',
        location: userLocation || undefined
      });

      trackSubscribe(email);
      markAsSubscribed();
      setSuccess(true);
      toast.success('Successfully subscribed!');

      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="subscribe-popup"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <button className="popup-close" onClick={onClose}>
              <FiX />
            </button>

            {success ? (
              <div className="popup-success">
                <div className="success-icon">
                  <FiCheck />
                </div>
                <h3>You're Subscribed!</h3>
                <p>Thank you for subscribing. You'll receive exclusive health tips and special offers from healthcare providers near you.</p>
              </div>
            ) : (
              <>
                <div className="popup-header">
                  <div className="popup-icon">
                    <FiMail />
                  </div>
                  <h3>Get Exclusive Health Tips & Discounts</h3>
                  <p>Subscribe to receive health tips, special discounts from nearby healthcare providers, and wellness insights delivered to your inbox.</p>
                </div>

                <div className="popup-benefits">
                  <div className="popup-benefit">
                    <FiHeart className="benefit-icon" />
                    <span>Personalized health tips</span>
                  </div>
                  <div className="popup-benefit">
                    <FiMapPin className="benefit-icon" />
                    <span>Offers from nearby providers</span>
                  </div>
                  <div className="popup-benefit">
                    <FiBookOpen className="benefit-icon" />
                    <span>Latest medical news</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="popup-form">
                  <div className="form-group">
                    <input
                      type="text"
                      className="input"
                      placeholder="Your name (optional)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      className="input"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {userLocation && (
                    <div className="popup-location">
                      <FiMapPin />
                      <span>{userLocation.city}{userLocation.country ? `, ${userLocation.country}` : ''}</span>
                    </div>
                  )}

                  <div className="interests-section">
                    <label className="interests-label">I'm interested in:</label>
                    <div className="interests-grid">
                      {interests.map(interest => (
                        <button
                          key={interest.id}
                          type="button"
                          className={`interest-chip ${selectedInterests.includes(interest.id) ? 'selected' : ''}`}
                          onClick={() => toggleInterest(interest.id)}
                        >
                          <interest.icon />
                          {interest.label}
                          {selectedInterests.includes(interest.id) && <FiCheck className="check" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg subscribe-btn"
                    disabled={loading}
                  >
                    {loading ? 'Subscribing...' : 'Subscribe & Get Health Tips'}
                  </button>

                  <p className="popup-disclaimer">
                    We respect your privacy. Unsubscribe anytime. No spam, ever.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SubscribePopup;
